/**
 * RevenueDashboardView — Phase 4 Payments (G.20). Lloyd-only revenue snapshot.
 * Self-gates: the backend returns 403 unless the manager has "can view payments", in which case
 * this shows an access message instead of the numbers.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import PaymentApi, {
  RevenueSummaryDto,
  RevenueChannelDto,
  RevenueClientBreakdownEntryDto,
  RevenuePeriod,
  PaymentsForbiddenError,
} from '../../api/PaymentApi';
import { formatCentsUsd } from '../../constants/paymentPresets';
import PaymentSearchPanel from '../../components/PaymentSearchPanel/PaymentSearchPanel';

const Metric: React.FC<{ label: string; value: string; hint?: string; onClick?: () => void }> = ({
  label,
  value,
  hint,
  onClick,
}) => (
  <Paper
    sx={{ p: 3, minWidth: 0, overflow: 'hidden', ...(onClick && { cursor: 'pointer' }) }}
    onClick={onClick}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    {...(onClick && { role: 'button', tabIndex: 0 })}
  >
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        mt: 1,
        wordBreak: 'break-word',
        fontSize: { xs: '1.5rem', sm: '2rem' },
        ...(onClick && { textDecoration: 'underline', textDecorationStyle: 'dotted' }),
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

/**
 * Which clients made up a clicked Today/This week total (Lloyd, 30 Sep 2026). Client and amount
 * only, no case link — his own scope.
 */
const RevenueBreakdownDialog: React.FC<{
  open: boolean;
  title: string;
  entries: RevenueClientBreakdownEntryDto[] | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}> = ({ open, title, entries, loading, error, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !entries || entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No payments in this period.
        </Typography>
      ) : (
        <List dense disablePadding>
          {entries.map((e) => (
            <ListItem key={e.clientName} disableGutters>
              <ListItemText primary={e.clientName} />
              <Typography variant="body2">{formatCentsUsd(e.amountCents)}</Typography>
            </ListItem>
          ))}
        </List>
      )}
    </DialogContent>
  </Dialog>
);

/**
 * One collection channel across the standard periods. Stripe and non-Stripe money are shown in
 * separate blocks rather than one figure: the Stripe number has to stay reconcilable against the
 * Stripe dashboard, and cash/Zelle/cheque money never appears there.
 */
const ChannelSection: React.FC<{
  title: string;
  caption?: string;
  channel: RevenueChannelDto;
  countLabel: string;
  onPeriodClick: (period: RevenuePeriod, title: string) => void;
}> = ({ title, caption, channel, countLabel, onPeriodClick }) => (
  <Box component="section">
    <Typography variant="h6" sx={{ mb: caption ? 0.5 : 2 }}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {caption}
      </Typography>
    )}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 2,
        alignContent: 'start',
      }}
    >
      <Metric
        label="Today"
        value={formatCentsUsd(channel.todayCents)}
        onClick={() => onPeriodClick('today', `${title} — today`)}
      />
      <Metric
        label="This week"
        value={formatCentsUsd(channel.weekCents)}
        onClick={() => onPeriodClick('week', `${title} — this week`)}
      />
      <Metric
        label="Last week"
        value={formatCentsUsd(channel.lastWeekCents)}
        hint="Monday to Sunday"
        onClick={() => onPeriodClick('lastWeek', `${title} — last week`)}
      />
      <Metric label="This month" value={formatCentsUsd(channel.monthCents)} />
      <Metric label="This year" value={formatCentsUsd(channel.yearCents)} />
      <Metric
        label="All time"
        value={formatCentsUsd(channel.allTimeCents)}
        hint={`${channel.paidLinkCount} ${countLabel}`}
      />
      {/*
        Money collected for a court and owed onward. Excluded from every figure above — shown here
        rather than silently dropped, because a total that quietly shrinks is a total that gets
        queried, and because what the firm owes a court is a real obligation worth seeing.
      */}
      {channel.passedThroughToCourtsCents > 0 && (
        <Metric
          label="Passed through to courts"
          value={formatCentsUsd(channel.passedThroughToCourtsCents)}
          hint="Collected for a court — not firm revenue, and not counted above"
        />
      )}
    </Box>
  </Box>
);

const RevenueDashboardView: React.FC = () => {
  const [summary, setSummary] = useState<RevenueSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownTitle, setBreakdownTitle] = useState('');
  const [breakdownEntries, setBreakdownEntries] = useState<RevenueClientBreakdownEntryDto[] | null>(
    null,
  );
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setSummary(await PaymentApi.getRevenueSummary());
      } catch (err) {
        if (err instanceof PaymentsForbiddenError) setForbidden(true);
        else setError(err instanceof Error ? err.message : 'Failed to load revenue');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openBreakdown = async (
    channelKind: 'total' | 'stripe' | 'outsideStripe',
    period: RevenuePeriod,
    title: string,
  ) => {
    setBreakdownTitle(title);
    setBreakdownOpen(true);
    setBreakdownLoading(true);
    setBreakdownError(null);
    setBreakdownEntries(null);
    try {
      setBreakdownEntries(await PaymentApi.getRevenueBreakdown(period, channelKind));
    } catch (err) {
      if (err instanceof PaymentsForbiddenError) setBreakdownError('Not permitted to view revenue');
      else setBreakdownError(err instanceof Error ? err.message : 'Failed to load breakdown');
    } finally {
      setBreakdownLoading(false);
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Revenue
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : forbidden ? (
          <Alert severity="info">You don't have access to revenue metrics.</Alert>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : summary ? (
          <Stack spacing={5}>
            <PaymentSearchPanel />

            <ChannelSection
              title="Total collected"
              caption="Every payment received on a case, however it arrived."
              channel={summary.collectedTotal}
              countLabel="payment(s)"
              onPeriodClick={(period, title) => openBreakdown('total', period, title)}
            />

            <ChannelSection
              title="Collected via Stripe"
              channel={summary.collectedViaStripe}
              countLabel="paid link(s)"
              onPeriodClick={(period, title) => openBreakdown('stripe', period, title)}
            />

            <ChannelSection
              title="Collected outside Stripe"
              caption="Cash, Zelle, checks and payments already taken in Clio, as recorded by staff."
              channel={summary.collectedOutsideStripe}
              countLabel="recorded payment(s)"
              onPeriodClick={(period, title) => openBreakdown('outsideStripe', period, title)}
            />

            <Box component="section">
              <Typography variant="h6" sx={{ mb: 2 }}>
                Amounts on record
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                  alignContent: 'start',
                }}
              >
                <Metric
                  label="Total on citations"
                  value={formatCentsUsd(summary.onRecord.citationAmountTotalCents)}
                  hint={`${summary.onRecord.citationCount} case(s) with an amount`}
                />
                <Metric
                  label="Pre-paid third-party"
                  value={formatCentsUsd(summary.onRecord.prepaidThirdPartyTotalCents)}
                  hint={`${summary.onRecord.prepaidThirdPartyCount} pre-paid case(s)`}
                />
              </Box>
            </Box>
          </Stack>
        ) : null}
      </Container>
      <RevenueBreakdownDialog
        open={breakdownOpen}
        title={breakdownTitle}
        entries={breakdownEntries}
        loading={breakdownLoading}
        error={breakdownError}
        onClose={() => setBreakdownOpen(false)}
      />
    </>
  );
};

export default RevenueDashboardView;
